import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Col,
  Input,
  Table,
  Spinner,
  CardFooter,
  Button,
} from 'reactstrap';
import { mdiAccountOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import useTableHook from '../../../components/hooks/useTableHook';
import { LEAVE_STATUS_BADGE_STYLES, otHeader } from './constants';
import { fetchEmployeeLeavesColumn } from '../../../store/apis/leaveService';
import { fetchEmployeeOvertime } from '../../../store/apis/overTimeService';
import {
  FilterDropdown,
  TableFilterBubbles,
  TableSortDisplayArrow,
  Badge,
  DisplayModal,
  UserAsyncAutocompleteSelect,
  TablePagination,
  DatePicker,
  GeneralAttachmentTableModal,
} from '../../../components/Common';
import { AuthUtil, FileUtil, StringUtil } from '../../../utils';
import * as icons from '../../../assets';
import { downloadBlobType } from '../../../store/apis/commonService';
import { fetchEmployeeData } from '../../../store/apis/employeeService';
import { Messages } from '../../../common/messages';

function OvertimeEnquiry() {
  document.title = 'Overtime Enquiry | Envision Daikin';
  const navigate = useNavigate();
  const [isDisplayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);
  const [viewAttachmentsModalOpen, setViewAttachmentsModalOpen] =
    useState(false);
  const [viewAttachmentModalConfig, setViewAttachmentModalConfig] = useState({
    attachmentData: [],
    type: '',
  });

  // Table Hook
  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    applyExternalFilters,
    isLoadingData,
    isLoadingColumn,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'TransNo',
      sortDirection: 'DESC',
      filters: [],
    },
    fetchFn: fetchEmployeeOvertime,
    fetchColumnFn: fetchEmployeeLeavesColumn,
    queryKeyPrefix: 'overTimeEnquiry',
    columnKeyPrefix: 'overTimeEnquiryColumn',
    tableName: 'OTEnquiry',
    defaultExternalFilters: {
      employeeId: {
        value: AuthUtil.getAuthUser()?.employeeId,
        label: AuthUtil.getAuthUser()?.userFullName,
      },
    },
  });

  const downloadFileMutatation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const handleAttachment = async (leaveData, type = 'view') => {
    if (leaveData?.AttachmentPath && leaveData?.AttachmentPath !== '-') {
      let attachmentsArray = [];
      try {
        attachmentsArray = StringUtil.checkStringOrJsonArray(
          leaveData?.AttachmentPath,
        );
        if (attachmentsArray.length === 1) {
          const fileBlob = await downloadFileMutatation.mutateAsync({
            fileName: attachmentsArray[0],
            type: 'OverTime',
          });
          const file = new File(
            [fileBlob],
            attachmentsArray[0] || 'attachment',
            { type: fileBlob.type },
          );
          if (type === 'view') {
            setDisplayModalFile(file);
            setDisplayModalOpen(true);
          } else {
            FileUtil.downloadBlobFile(
              file,
              attachmentsArray[0] || 'attachment',
            );
          }
        } else {
          setViewAttachmentsModalOpen(true);
          setViewAttachmentModalConfig({
            attachmentData: attachmentsArray,
            type: 'OverTime',
          });
        }
      } catch (error) {
        if (type === 'view') {
          toast.error('Failed to fetch attachment for preview.', {
            className: 'toast-error',
          });
        } else {
          toast.error('Failed to fetch attachment for download.', {
            className: 'toast-error',
          });
        }
      }
    } else if (type === 'view') {
      toast.error('No attachment found to preview.', {
        className: 'toast-error',
      });
    } else {
      toast.error('No attachment found to download.', {
        className: 'toast-error',
      });
    }
  };

  const handleLinkToView = item => {
    navigate(`/overtime/apply?ID=${item?.TransNo}`);
  };

  const renderTableBody = () => {
    const tableData = data?.data || [];

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={12}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_OVERTIME
            )}
          </td>
        </tr>
      );
    }

    return tableData.map(item => (
      <tr
        key={item.TransNo}
        onClick={() => handleLinkToView(item)}
        className="cursor-pointer"
      >
        <td className="cursor-pointer text-decoration-underline text-primary text-header">
          {item.TransNo}
        </td>
        <td>{item.Date}</td>
        <td>{item.DayStatus}</td>
        <td>{item.Category}</td>
        <td>{item.FromTime || '-'}</td>
        <td>{item.ToTime || '-'}</td>
        <td>{item['OT Hours'] || '-'}</td>
        <td title={item.Reason}>{item.Reason}</td>
        <td>
          <Badge
            config={LEAVE_STATUS_BADGE_STYLES[item.Status]}
            labelType="TitleCase"
            badgeType="pill"
            dot
          >
            {item.Status}
          </Badge>
        </td>
        <td className="align-items-center gap-2">
          <span>{item.Approver}</span>
        </td>
        <td>{item.PayPeriod || '-'}</td>
        <td className="text-center">
          <div className="d-flex flex-row align-items-center justify-content-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0"
              title="View Leave"
              aria-label="View Leave"
              onClick={e => {
                e.stopPropagation();
                handleAttachment(item, 'view');
              }}
            >
              <img
                src={icons.iconsEnquiryviewicon}
                alt=""
                style={{ height: '12px' }}
              />
            </button>
            <button
              type="button"
              className="btn btn-link p-0"
              title="Download Leave"
              aria-label="Download Leave"
              onClick={e => {
                e.stopPropagation();
                handleAttachment(item, 'download');
              }}
            >
              <img
                src={icons.iconsEnquirydlicon}
                alt=""
                style={{ height: '12px' }}
              />
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  const externalFiltersValidation = () => {
    if (
      externalFilters.otFromDate &&
      externalFilters.otToDate &&
      new Date(externalFilters?.otFromDate) >
        new Date(externalFilters?.otToDate)
    ) {
      toast.error('To Date should be greater than equal to From Date.', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  return (
    <div className="page-content main-table-page">
      <Card className="enquiry-card">
        <CardBody className="p-0">
          <div className="p-3">
            <span className="fw-semibold fs-5">Overtime Enquiry</span>
          </div>
          <div className="external-filter gap-2 mx-3 p-3">
            <Col className="external-filter-col">
              <b>Employee</b>
              <UserAsyncAutocompleteSelect
                autoCompleteFn={() => fetchEmployeeData()}
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.Code,
                    label: item.Description,
                    name: item.Description,
                    code: item.Code,
                    designation: item.Designation,
                    image: item?.Imagefile,
                  }))
                }
                onChange={e => {
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    employeeId: e,
                  }));
                }}
                placeholder="Search by Employee Name & ID"
                value={externalFilters?.employeeId}
                inputIcon={
                  <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                }
                defaultShowMenu
                rightKeyAssessor="code"
                searchEmployee={
                  AuthUtil.getAuthUser()?.employeeId?.toLowerCase() !==
                  AuthUtil.getAuthUser()?.userName?.toLowerCase()
                }
                cursorType="default"
                isClearable={false}
                isSearchable={false}
              />
            </Col>
            <Col className="external-filter-col">
              <b>OT Category</b>
              <Input
                className="form-control external-filter-input"
                type="select"
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    otCategory: e.target.value,
                  }))
                }
              >
                <option value="">Type</option>
                <option value="In Office">In Office</option>
                <option value="Out Of Office">Out of Office</option>
              </Input>
            </Col>
            <Col className="external-filter-col">
              <b>OT From Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    otFromDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.otFromDate || ''}
              />
            </Col>
            <Col className="external-filter-col">
              <b>OT To Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    otToDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.otToDate || ''}
              />
            </Col>
            <Col className="d-flex align-items-center justify-content-end">
              <Button
                type="button"
                className="custom-btn"
                onClick={() => {
                  if (!externalFiltersValidation()) {
                    return;
                  }
                  applyExternalFilters({
                    ...externalFilters,
                    employeeId: externalFilters?.employeeId?.value,
                  });
                }}
              >
                Search
              </Button>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={otHeader}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive" id="overtime-enquiry">
            <Table hover className="custom-table align-middle text-nowrap">
              <thead>
                <tr>
                  {Object.entries(otHeader).map(([key, value]) => (
                    <th key={key}>
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => {
                            handleAutoSort(key);
                          }}
                        >
                          {value}
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <TableSortDisplayArrow
                            direction={checkColumnIfSorted(key)}
                          />
                          <FilterDropdown
                            handleSetColumnName={() => {
                              setColumnName(key);
                            }}
                            setColumnName={setColumnName}
                            columnData={columnData}
                            applyFilter={applyFilter}
                            columnName={columnName}
                            pageRequest={pageRequest}
                            setPageRequest={setPageRequest}
                            handleManualSort={handleManualSort}
                            isLoading={isLoadingColumn}
                          />
                        </div>
                      </div>
                    </th>
                  ))}
                  <th className="text-center">Attachments</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
      <DisplayModal
        isModalOpen={isDisplayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
      <GeneralAttachmentTableModal
        isModalOpen={viewAttachmentsModalOpen}
        toggle={() => setViewAttachmentsModalOpen(false)}
        attachmentData={viewAttachmentModalConfig.attachmentData}
        type={viewAttachmentModalConfig?.type}
        hrCoCd={viewAttachmentModalConfig?.hrCoCd}
      />
    </div>
  );
}

export default OvertimeEnquiry;
